import { useCallback, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { DataCard } from '@fashionnovateam/nova-ui-toolkit';
import { VirtuosoGrid } from 'react-virtuoso';
import { VendorCatalogItem } from '../../Lib/Hooks/ItemService';
import { PageLoading } from '../PageLoading/PageLoading';
import { useGetItemPhotoURLs } from '../../Lib/Hooks/ItemService/useGetItemPhotoURLs';

type ItemCardProps = {
  item: VendorCatalogItem;
};

function ItemCard({ item }: ItemCardProps) {
  const location = useLocation();
  const history = useHistory();

  const userId = useMemo(() => location.pathname.split('/')[2], [location]);

  const { data: photoUrls } = useGetItemPhotoURLs(userId, item.id);

  const frontImage = photoUrls?.front_photo;

  const handleCardClick = useCallback(() => {
    history.push(`/users/${userId}/sell-to-us/${item.id}${location.search}`);
  }, [location.search, userId, history]);

  if (!item) return null;

  return (
    <div className="w-full bg-white flex flex-col flex-shrink-0 flex-grow-0 min-h-[400px] h-auto relative">
      <DataCard
        onCardClick={handleCardClick}
        data-testid={`item-card-item-${item.id}`}
        key={item.id}
        images={[
          {
            src: frontImage?.thumbnail_url || frontImage?.url || '',
            fallbackSrc: frontImage?.url || '',
          },
        ]}
        title={item.style_number}
      />
    </div>
  );
}

type ItemCardListProps = {
  items: VendorCatalogItem[];
  fetchNextPage: () => void;
  hasNextPage: boolean | undefined;
  isFetchingNextPage: boolean;
  isLoading: boolean;
};

function ItemCardList({
  items,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  isLoading,
}: ItemCardListProps) {
  if (isLoading) {
    return <PageLoading />;
  }

  return (
    <div className="w-full h-full p-4">
      <VirtuosoGrid
        data-testid="item-card-list"
        totalCount={items.length || 0}
        overscan={400}
        listClassName="w-full grid auto-rows-min box-border grid-cols-1 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 [&]:[@media(min-width:3715px)]:grid-cols-[repeat(auto-fill,minmax(300px,1fr))] gap-4 p-4 flex-1"
        style={{
          height: '100%',
          overflow: 'auto',
        }}
        itemClassName="w-full"
        endReached={() => {
          if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
          }
        }}
        itemContent={(index) => {
          const item = items[index];
          if (!item) return null;

          return <ItemCard item={item} />;
        }}
        components={{
          Footer: () =>
            isFetchingNextPage ? (
              <div className="w-full flex justify-center py-4">
                <div className="w-6 h-6 border-2 border-neutral-300 border-t-primary-500 rounded-full animate-spin" />
              </div>
            ) : (
              <div style={{ height: '32px' }} />
            ),
        }}
      />
    </div>
  );
}

export default ItemCardList;
